import Divider from "@/components/Divider";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuthActions, useAuthMessages } from "@/context/auth/AuthContext";
import { querykeys } from "@/hooks/react-query/constants";
import useMarkNotificationsReadRest from "@/hooks/react-query/Notifications/useMarkNotificationsReadRest";
import useGetNotificationsRest from "@/hooks/react-query/Notifications/useGetNotificationsRest";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/utils/formatDate";
import { ErrorMapper } from "@/utils/errorMapping";
import { Bell, BellOff, CircleSmall, Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import {
  describirNotificacion,
  getLink,
  NOTIFICATION_TYPES,
} from "./utils/utils";

interface NotificationsProps {
  /**
   * Estado del socket, que llega **desde arriba** a propósito.
   *
   * Antes este componente llamaba a `useSocket()`. `TopBar` lo monta dos veces
   * —la variante de desktop y la de mobile, que CSS oculta pero React monta
   * igual—, así que cada pestaña abría **dos** conexiones a `/messaging/ws`.
   * Ahora el hook vive una sola vez en `TopBar`. Ver [BUG-42].
   */
  socketStatus: string;
}

function Notifications({ socketStatus }: NotificationsProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notifications, items } = useGetNotificationsRest([], true, [], false);
  const markRead = useMarkNotificationsReadRest();
  const messages = useAuthMessages();
  const { setMessages } = useAuthActions();
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (notifications.isSuccess) {
      const prevMessages = [
        ...items.map((originalNotif: any) => {
          if (
            messages.find(
              (notif: any) =>
                notif.notificationId === originalNotif.notificationId
            )
          ) {
            if (originalNotif.status !== "read") {
              return messages.find(
                (notif: any) =>
                  notif.notificationId === originalNotif.notificationId
              );
            } else {
              return null;
            }
          } else {
            return {
              ...originalNotif,
              notification: {
                ...originalNotif.notification,
              },
            };
          }
        }),
      ]
        .filter(Boolean)
        .filter((message) => message.status !== "read");

      // filter by createdAt desc
      const newMessagesFiltered = prevMessages.sort((a, b) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });

      setMessages(newMessagesFiltered);
    }
  }, [notifications.isSuccess, notifications.isRefetching]);

  const markAsRead = async () => {
    try {
      setLoading(true);
      await markRead.mutateAsync(
        messages.map((message: any) => message.notificationId)
      );

      setMessages([]);
      await queryClient.invalidateQueries([querykeys.notifications]);
      await queryClient.refetchQueries([querykeys.notifications]);
      setLoading(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `No se pudo marcar como leídas las notificaciones. ${
          error.errorCode ? ErrorMapper.getMessage(error.errorCode) : ""
        }`,
        variant: "error",
        duration: 5000,
      });
      setLoading(false);
    }
  };

  const getConnectionIcon = () => {
    switch (socketStatus) {
      case "Connected":
        return "text-text-primary";
      case "Reconnecting":
        return "text-text-secondary";
      case "Disconnected":
        return "text-error-foreground";
      case "Error":
        return "text-error-light";
      default:
        return "text-text-primary";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative z-100 cursor-pointer">
          {messages.length > 0 && (
            <div className="absolute min-w-[15px] h-[15px] text-[10px] text-white bg-red-500 rounded-[10px] -top-[5px] -right-[5px] px-[5px] z-2">
              {messages.filter((message: any) => message.status !== "read")
                .length > 50
                ? "+50"
                : messages.filter((message: any) => message.status !== "read")
                    .length}
            </div>
          )}
          <div className="relative z-1">
            <Bell
              className={`icon hover:text-brand-primary transition ${getConnectionIcon()}`}
            />
            {/* <div>{getConnectionIcon()}</div> */}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 scrollbar-thin pb-0 mx-4 max-h-[350px] overflow-y-auto rounded-lg border border-border bg-background-100 shadow-lg shadow-black/10 dark:bg-background-200 dark:border-border-200 dark:shadow-none">
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 pb-3">
            <h4 className="text-sm font-semibold text-text-primary">
              Notificaciones
            </h4>

            {messages.length > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-text-primary transition disabled:opacity-50"
                onClick={markAsRead}
                disabled={loading}
              >
                {loading && <Loader2Icon className="h-3 w-3 animate-spin" />}
                <span>Marcar todo como leído</span>
              </button>
            )}
          </div>

          <Divider />

          {/* Content */}
          {messages.length > 0 ? (
            <div className="divide-y divide-border">
              {messages
                .sort(
                  (a: any, b: any) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                )
                .map((message: any) => (
                  <button
                    type="button"
                    key={message.id}
                    onClick={async () => {
                      const link = getLink(message);

                      await markRead.mutateAsync([message.notificationId]);

                      setMessages(
                        messages
                          .map((msg: any) => {
                            if (msg.notificationId === message.notificationId) {
                              return null;
                            }

                            return msg;
                          })
                          .filter(Boolean)
                      );

                      // Refetch B2B orders when clicking a B2B notification
                      if (
                        message.notification?.type ===
                          NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_STATUS_CHANGED ||
                        message.notification?.type ===
                          NOTIFICATION_TYPES.BIG_SUPPLIER_B2B_ORDER_RECEIVED
                      ) {
                        queryClient.invalidateQueries(querykeys.b2bOrders);
                      }

                      setIsOpen(false);
                      navigate(link);
                    }}
                    className="w-full px-2 py-3 flex items-start gap-2.5 text-left hover:bg-background-200 transition"
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <CircleSmall className="h-3 w-3 text-brand-primary" />
                    </div>

                    <div className="flex flex-col gap-0.5 min-w-0">
                      <p
                        className={`text-sm leading-tight ${
                          message.status !== "read"
                            ? "font-medium text-text-primary"
                            : "font-normal text-text-secondary"
                        }`}
                      >
                        {message.notification?.title ||
                          describirNotificacion(message.notification)?.title ||
                          "Notificación"}
                      </p>
                      {describirNotificacion(message.notification)
                        ?.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {
                            describirNotificacion(message.notification)
                              ?.description
                          }
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDate(
                          message.createdAt,
                          "dd/MM/yyyy HH:mm'hs",
                          false
                        )}
                      </p>
                    </div>
                  </button>
                ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-background-200 mb-3">
                <BellOff className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-text-primary mb-1">
                Sin notificaciones
              </p>
              <p className="text-xs text-muted-foreground text-center">
                No tenés notificaciones pendientes
              </p>
            </div>
          )}

          {/* Footer */}
          <button
            type="button"
            className="sticky bottom-0 w-full px-4 py-2.5 bg-background-100 border-t border-border text-center text-xs font-medium text-muted-foreground hover:text-text-primary hover:bg-background-200 transition"
            onClick={() => {
              setIsOpen(false);
              navigate("/notificaciones");
            }}
          >
            Ver todas las notificaciones
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default Notifications;
